const AUTH_REMOTE_BASE_URL = (
  import.meta.env.VITE_AUTH_REMOTE_URL || "https://runwayapi.cityoptima.com"
).replace(/\/$/, "");
export const authApiBaseUrl = (
  import.meta.env.VITE_AUTH_BASE_URL ||
  (import.meta.env.DEV ? "/auth-api" : AUTH_REMOTE_BASE_URL)
).replace(/\/$/, "");
const AUTH_BASE_URL = authApiBaseUrl;

type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  token?: string | null;
  body?: unknown;
};

export type PositionOption = {
  _id: string;
  key: string;
  label: string;
  description?: string;
};

export type AirportRecord = {
  _id: string;
  name: string;
  code: string;
  city?: string;
  country?: string;
  isActive?: boolean;
};

export type AuthUserRecord = {
  _id: string;
  firstName: string;
  lastName: string;
  preferredName: string;
  employee_id: string;
  email?: string | null;
  position?: PositionOption | string | null;
  airport?: AirportRecord | string | null;
};

export type WorkerRecord = {
  _id: string;
  firstName: string;
  lastName: string;
  preferredName?: string;
  employee_id: string;
  email?: string | null;
  gender?: "male" | "female" | null;
  duty_class?: DutyClassInput;
  handle_gender_task?: "male" | "female" | "both" | null;
  has_previlage?: boolean | null;
  position?: PositionOption | string | null;
  airport?: AirportRecord | string | null;
  lines?: Array<
    | string
    | {
        _id?: string;
        key?: string;
        label?: string;
        name?: string;
      }
  > | null;
};

export type DutyClassRef = {
  _id?: string;
  key?: string;
  label?: string;
};

export type DutyClassValue = string;
export type DutyClassInput =
  | DutyClassValue[]
  | DutyClassValue
  | DutyClassRef[]
  | DutyClassRef
  | "ld/hd"
  | null;

export type WorkAreaAssignmentInput = {
  work_area: string;
  shift_definition?: string | null;
};

export type RegisterRequest = {
  firstName: string;
  lastName: string;
  preferredName: string;
  employee_id: string;
  email?: string | null;
  password: string;
  gender?: "male" | "female";
  duty_class?: DutyClassInput;
  handle_gender_task?: "male" | "female" | "both";
  work_areas?: WorkAreaAssignmentInput[] | null;
  lines?: string[] | null;
  has_previlage?: boolean;
  position?: string;
  airportId?: string | null;
};

export type LoginRequest = {
  employee_id: string;
  password: string;
};

export type AirportUpsertRequest = {
  name?: string;
  code?: string;
  city?: string;
  country?: string;
  isActive?: boolean;
};

export type UserUpdateRequest = {
  firstName?: string;
  lastName?: string;
  preferredName?: string;
  employee_id?: string;
  gender?: "male" | "female";
  duty_class?: DutyClassInput;
  handle_gender_task?: "male" | "female" | "both";
  work_areas?: WorkAreaAssignmentInput[] | null;
  lines?: string[] | null;
  has_previlage?: boolean;
  email?: string;
  password?: string;
  position?: string;
  airportId?: string | null;
};

type ApiErrorPayload = {
  message?: string;
  error?: string;
};

type HealthResponse = { status: string };
type PositionsResponse = { positions: PositionOption[] };
type AuthResponse = {
  token: string;
  expiresIn?: string;
  user: AuthUserRecord;
  message?: string;
};
type AirportsResponse = { airports: AirportRecord[] };
type AirportResponse = { airport: AirportRecord; message?: string };
type UserResponse = { user: AuthUserRecord; message?: string };
type WorkersResponse = { count: number; workers: WorkerRecord[] };
type DeleteResponse = { message?: string };

function getToken(explicitToken?: string | null) {
  if (explicitToken !== undefined) return explicitToken;
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem("serviceToken");
}

async function request<T>(
  path: string,
  { method = "GET", token, body }: RequestOptions = {},
) {
  const resolvedToken = getToken(token);
  const headers: Record<string, string> = {};

  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (resolvedToken) {
    headers.Authorization = `Bearer ${resolvedToken}`;
  }

  const response = await fetch(`${AUTH_BASE_URL}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const raw = await response.text();
  let parsed: unknown = null;

  if (raw) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = null;
    }
  }

  if (!response.ok) {
    const payload = (parsed || {}) as ApiErrorPayload;
    throw new Error(
      payload.message ||
        payload.error ||
        response.statusText ||
        "Request failed",
    );
  }

  return (parsed || {}) as T;
}

export async function healthCheck() {
  return request<HealthResponse>("/health");
}

export async function getPositions() {
  const response = await request<PositionsResponse>("/api/positions");
  return response.positions || [];
}

export async function registerUser(payload: RegisterRequest) {
  return request<AuthResponse>("/api/auth/register", {
    method: "POST",
    body: payload,
  });
}

export async function loginUser(payload: LoginRequest) {
  return request<AuthResponse>("/api/auth/login", {
    method: "POST",
    body: payload,
  });
}

export async function listAirports(token?: string | null) {
  const response = await request<AirportsResponse>("/api/airports", { token });
  return response.airports || [];
}

export async function createAirport(
  payload: AirportUpsertRequest,
  token?: string | null,
) {
  const response = await request<AirportResponse>("/api/airports", {
    method: "POST",
    token,
    body: payload,
  });
  return response.airport;
}

export async function updateAirport(
  airportId: string,
  payload: AirportUpsertRequest,
  token?: string | null,
) {
  const response = await request<AirportResponse>(
    `/api/airports/${airportId}`,
    {
      method: "PUT",
      token,
      body: payload,
    },
  );
  return response.airport;
}

export async function updateUser(
  userId: string,
  payload: UserUpdateRequest,
  token?: string | null,
) {
  const response = await request<UserResponse>(`/api/users/${userId}`, {
    method: "PUT",
    token,
    body: payload,
  });
  return response.user;
}

export async function deleteUser(userId: string, token?: string | null) {
  return request<DeleteResponse>(`/api/users/${userId}`, {
    method: "DELETE",
    token,
  });
}

export async function listWorkersByAirportAndPosition(
  airportId: string,
  position: string,
  token?: string | null,
) {
  const query = new URLSearchParams({ airportId, position }).toString();
  const response = await request<WorkersResponse>(
    `/api/users/workers?${query}`,
    { token },
  );
  return response.workers || [];
}
